import { useState } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';

type Props = {
  mode: 'approve' | 'reject';
  changeTitle: string;
  isPending: boolean;
  error: unknown;
  /** For approve: optional comments. For reject: the (required) reason. */
  onConfirm: (text: string) => void;
  onClose: () => void;
};

/**
 * Decision dialog for an ICN. Reject requires a non-empty reason (the API's
 * `rejection_reason`); approve takes optional comments. Surfaces the API error
 * inline so a failed action is never silent (#5/#11).
 */
export function ChangeDecisionDialog({
  mode,
  changeTitle,
  isPending,
  error,
  onConfirm,
  onClose,
}: Props) {
  const [text, setText] = useState('');
  const isReject = mode === 'reject';
  const trimmed = text.trim();
  const canSubmit = isReject ? trimmed.length > 0 : true;
  const errorMessage = error instanceof Error ? error.message : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="decision-dialog-title"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-lg">
        <div className="flex items-center gap-2">
          {isReject ? (
            <XCircle className="h-5 w-5 text-destructive" />
          ) : (
            <CheckCircle className="h-5 w-5 text-green-600" />
          )}
          <h3 id="decision-dialog-title" className="text-lg font-semibold text-card-foreground">
            {isReject ? 'Rechazar solicitud' : 'Aprobar solicitud'}
          </h3>
        </div>

        <p className="mt-2 text-sm text-muted-foreground">
          {isReject ? 'Vas a rechazar' : 'Vas a aprobar'}{' '}
          <span className="font-medium text-card-foreground">{changeTitle}</span>.
        </p>

        <label
          htmlFor="decision-dialog-text"
          className="mt-4 block text-sm font-medium text-foreground"
        >
          {isReject ? 'Motivo del rechazo' : 'Comentarios (opcional)'}
        </label>
        <textarea
          id="decision-dialog-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          autoFocus
          placeholder={
            isReject ? 'Explica por qué se rechaza esta solicitud…' : 'Agrega un comentario…'
          }
          className="mt-1.5 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {isReject && (
          <p className="mt-1 text-xs text-muted-foreground">El motivo es obligatorio.</p>
        )}

        {errorMessage && (
          <p className="mt-3 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {errorMessage}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(trimmed)}
            disabled={isPending || !canSubmit}
            className={
              isReject
                ? 'rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:opacity-50'
                : 'rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50'
            }
          >
            {isPending ? 'Procesando…' : isReject ? 'Rechazar' : 'Aprobar'}
          </button>
        </div>
      </div>
    </div>
  );
}
